/**
 * R268s · Feb 2026 — Printable-format HTML preview for GST documents.
 *
 * One component that mirrors the ReportLab PDF layout used by
 * routes_gst_pdf.py so users can:
 *   • See exactly what an estimate / SO / invoice / CN / bill will
 *     look like after export — inside the Detail dialog.
 *   • Live-preview colour / layout / font tweaks on the org Billing
 *     Setup page.
 *
 * The component is intentionally CSS-only (no HTML-to-PDF conversion)
 * — the printable HTML shares the same visual grammar as the PDF
 * (header band, parties row, striped table, totals block, footer),
 * so what the user sees here matches the printed page 1:1.
 *
 * Props:
 *   doc        — the gst_documents row (or a sample). Required.
 *   profile    — org_billing_profiles row (colours, layout, terms).
 *   compact    — true → dense/scaled-down preview (Billing Setup).
 */
import { useMemo } from "react";
import { fmtMoney } from "@/lib/currency";

const _TITLE = {
  invoice:         "TAX INVOICE",
  credit_note:     "CREDIT NOTE",
  // Feb 2026 R293zp · Shortened to just "QUOTATION".
  estimate:        "QUOTATION",
  sales_order:     "SALES ORDER",
  vendor_bill:     "VENDOR BILL",
  debit_note:      "DEBIT NOTE",
  payment_receipt: "PAYMENT RECEIPT",
};

const _PRESETS = {
  classic:  { primary: "#111827", accent: "#374151" },
  modern:   { primary: "#4F46E5", accent: "#818CF8" },
  minimal:  { primary: "#000000", accent: "#525252" },
  bordered: { primary: "#166534", accent: "#16A34A" },
};

function inr(n) {
  // Kept for backward compat with sample data — prefer `fmt(...)` below.
  const num = Number(n || 0);
  return "₹" + num.toLocaleString("en-IN", {
    maximumFractionDigits: 2, minimumFractionDigits: 2,
  });
}

export default function DocumentPreview({ doc, profile, compact }) {
  const preset = _PRESETS[profile?.layout_preset || "modern"] || _PRESETS.modern;
  const primary = profile?.primary_color || preset.primary;
  const accent  = profile?.secondary_color || preset.accent;
  const fontFam = profile?.font_family || "Inter";
  const layout  = profile?.layout_preset || "modern";
  // R268v · Feb 2026 — GST-off mode: hide GSTIN / state / HSN /
  // CGST-SGST-IGST rows and drop "Tax" from the title.
  const gstOn = profile?.gst_registered !== false;
  // R268w · Feb 2026 — Multi-currency: use whichever currency the doc
  // was raised in. Falls back to INR for legacy docs that predate the
  // toggle. Every money slot in the preview goes through `fmt()`.
  const currency = doc?.currency || doc?.totals?.currency || "INR";
  // Feb 2026 · Exports are zero-rated under GST. When the doc is raised
  // in a currency ≠ the workspace home currency, suppress the GST rows
  // (CGST/SGST/IGST/cess/TDS-under-GST) so the preview mirrors what the
  // PDF prints for foreign customers. The GSTIN / state-code columns
  // stay honest to the registered details.
  const homeCurrency = profile?.home_currency || "INR";
  const isForeign = currency && currency !== homeCurrency;
  const showGstRows = gstOn && !isForeign;
  // Feb 2026 R293zu · Role-aware totals — when the backend has stripped
  // monetary fields (viewer lacks estimate.view_rates / package.view_rates
  // / quotation.view / invoice.view), every money slot renders as "—"
  // instead of ₹0 so the gate reads intentional. Normal viewers go
  // straight through fmtMoney.
  const ratesHidden = !!doc?._rates_hidden;
  const fmt = ratesHidden
    ? (() => "—")
    : (n) => fmtMoney(n, currency);

  const title = gstOn
    ? (_TITLE[doc?.doc_type] || "DOCUMENT")
    : (_TITLE[doc?.doc_type] || "DOCUMENT").replace(/^TAX\s+/, "");
  const kind  = doc?.doc_type === "credit_note" ? "credit_note"
              : doc?.doc_type === "estimate" || doc?.doc_type === "sales_order" ? "quote"
              : "invoice";
  const header = profile?.[`${kind}_header`];
  const terms  = profile?.[`${kind}_terms`];
  const footer = profile?.[`${kind}_footer`];

  const t = doc?.totals || {};

  // Clean up the dash-only version introduced above — we no longer
  // need a separate fmtOrDash since fmt itself honours ratesHidden.

  // Feb 2026 R293zs · DRAFT highlight on the preview so accounts
  // don't mistake a working-copy for an approved document. Any doc
  // in the approval workflow stays "draft" until approval_status
  // flips to `approved`; legacy / non-approval docs fall back to
  // the lifecycle `status === "draft"` check. Matches the diagonal
  // stamp the exported PDF already prints on draft docs.
  const _appr = (doc?.approval_status || "").toLowerCase();
  const isDraft = _appr
    ? _appr !== "approved"
    : (doc?.status || "").toLowerCase() === "draft";

  const rootStyle = useMemo(() => ({
    fontFamily: `"${fontFam}", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
    fontSize: compact ? "10px" : "12px",
    lineHeight: 1.4,
    color: "#111827",
    background: "white",
    // "Bordered" preset — a full-page border wrap.
    border: layout === "bordered" ? `2px solid ${primary}` : "1px solid #e5e7eb",
    borderRadius: layout === "modern" ? "6px" : "0",
    padding: compact ? "10px 12px" : "18px 22px",
    boxShadow: layout === "modern" ? "0 1px 2px rgba(0,0,0,.06)" : "none",
    position: "relative",
    overflow: "hidden",
  }), [primary, layout, fontFam, compact]);

  const brandBar = layout === "minimal"
    ? null
    : { height: compact ? "4px" : "6px", background: primary, margin: compact ? "-10px -12px 10px" : "-18px -22px 14px" };

  return (
    <div data-testid="doc-preview" style={rootStyle}>
      {brandBar && <div style={brandBar} />}

      {/* Feb 2026 R293zs · DRAFT top banner — unmissable, red + solid */}
      {isDraft && (
        <div
          data-testid="doc-preview-draft-banner"
          style={{
            background: "#fef2f2",
            border: "1px solid #ef4444",
            color: "#991b1b",
            fontWeight: 700,
            fontSize: compact ? "10px" : "12px",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            padding: compact ? "4px 8px" : "6px 10px",
            borderRadius: 4,
            textAlign: "center",
            marginBottom: compact ? 8 : 12,
          }}
        >
          Draft · not approved · not for payment
        </div>
      )}

      {/* Feb 2026 R293zs · Diagonal watermark — mirrors the PDF stamp
          so what you see here is what the counterparty would see if
          someone forwarded a draft by mistake. Pointer-events off so
          it never blocks selection or clicks. */}
      {isDraft && (
        <div
          data-testid="doc-preview-draft-watermark"
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <div
            style={{
              transform: "rotate(-28deg)",
              fontSize: compact ? "80px" : "140px",
              fontWeight: 900,
              color: "rgba(239, 68, 68, 0.12)",
              letterSpacing: 8,
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            DRAFT
          </div>
        </div>
      )}

      {/* Header row: entity ↔ doc no / date */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {profile?.logo_url && (
            <img src={profile.logo_url} alt="" style={{ maxHeight: compact ? 22 : 36, maxWidth: 120, marginBottom: 4 }} />
          )}
          <div style={{ fontWeight: 700, fontSize: compact ? "12px" : "15px", color: primary }}>
            {profile?.legal_name || profile?.trade_name || "Your Company Name"}
          </div>
          {profile?.gstin && gstOn && (
            <div style={{ color: accent, fontSize: compact ? "9px" : "11px" }}>
              GSTIN: {profile.gstin}
              {profile?.pan && <>  ·  PAN: {profile.pan}</>}
            </div>
          )}
          {!gstOn && profile?.pan && (
            <div style={{ color: accent, fontSize: compact ? "9px" : "11px" }}>
              PAN: {profile.pan}
            </div>
          )}
          {profile?.address && (
            <div style={{ color: "#374151", fontSize: compact ? "9px" : "10.5px", whiteSpace: "pre-line" }}>
              {profile.address}
            </div>
          )}
          {(profile?.email || profile?.phone) && (
            <div style={{ color: "#6b7280", fontSize: compact ? "9px" : "10px" }}>
              {profile?.email}{profile?.email && profile?.phone ? " · " : ""}{profile?.phone}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: compact ? "12px" : "16px", color: primary, letterSpacing: 0.5 }}>
            {title}
          </div>
          <div style={{ fontSize: compact ? "9px" : "11px", color: "#374151", marginTop: 2 }}>
            No: <b>{doc?.doc_no || "—"}</b><br />
            Date: {doc?.doc_date || "—"}
            {doc?.due_date && <><br />Due: {doc.due_date}</>}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: primary, opacity: 0.4, margin: compact ? "8px 0" : "12px 0" }} />

      {/* Feb 2026 R293v · Subject line — mirrors the PDF so users
          see the full doc exactly as the customer will. */}
      {doc?.subject && (
        <div style={{ fontSize: compact ? "10px" : "12px", color: "#111827", marginBottom: compact ? 6 : 10 }}>
          <b>Subject:</b> {doc.subject}
        </div>
      )}

      {/* Optional doc-header terms */}
      {header && (
        <div style={{ fontSize: compact ? "9px" : "11px", color: "#374151", marginBottom: compact ? 6 : 10, fontStyle: "italic" }}>
          {header}
        </div>
      )}

      {/* Parties row */}
      <div style={{ display: "grid", gridTemplateColumns: gstOn ? "1fr 1fr 1fr" : "1fr 1fr", gap: 6, marginBottom: compact ? 8 : 12 }}>
        {[
          { label: "Bill to", body: (
            <>
              <b>{doc?.customer_name || "Customer name"}</b>
              {gstOn && doc?.customer_gstin && <><br />GSTIN: {doc.customer_gstin}</>}
              {doc?.customer_billing_address && <><br /><span style={{ whiteSpace: "pre-line" }}>{doc.customer_billing_address}</span></>}
            </>
          )},
          { label: "Supplier", body: (
            <>
              <b>{profile?.legal_name || "Your Company"}</b>
              {gstOn && profile?.gstin && <><br />GSTIN: {profile.gstin}</>}
              {gstOn && profile?.state_code && <><br />State: {profile.state_code}</>}
              {!gstOn && profile?.pan && <><br />PAN: {profile.pan}</>}
            </>
          )},
          gstOn && { label: "Supply", body: (
            <>
              Place: {doc?.place_of_supply_code || doc?.customer_state_code || "—"}<br />
              {t.is_intra_state ? "Intra-state" : "Inter-state"}
              {doc?.reverse_charge && <><br />Reverse charge: <b>YES</b></>}
            </>
          )},
        ].filter(Boolean).map((box, i) => (
          <div key={i} style={{ border: "1px solid #e5e7eb", padding: compact ? "5px 6px" : "8px 10px", borderRadius: 3, fontSize: compact ? "8.5px" : "10.5px" }}>
            <div style={{ color: accent, fontSize: compact ? "8px" : "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
              {box.label}
            </div>
            <div>{box.body}</div>
          </div>
        ))}
      </div>

      {/* Line items table */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 3, overflow: "hidden", marginBottom: compact ? 8 : 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: compact ? "8.5px" : "10.5px" }}>
          <thead>
            <tr style={{ background: primary, color: "white" }}>
              {(gstOn
                  ? ["#", "HSN/SAC", "Description", "Qty", "Rate", "Taxable", "GST %", "GST", "Total"]
                  : ["#", "Description", "Qty", "Rate", "Amount"]
              ).map((h, i) => {
                // Right-align numeric cols. Alignment index differs by mode.
                const rightAligned = gstOn ? i >= 3 : i >= 2;
                return (
                  <th key={i} style={{ padding: compact ? "4px 5px" : "6px 8px", textAlign: rightAligned ? "right" : "left", fontWeight: 700, fontSize: compact ? "8px" : "10px" }}>
                    {h}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(doc?.lines || []).map((l, i) => (
              <tr key={i} style={{ background: i % 2 ? "#f9fafb" : "white" }}>
                <td style={{ padding: compact ? "3px 5px" : "5px 8px" }}>{i + 1}</td>
                {gstOn && (
                  <td style={{ padding: compact ? "3px 5px" : "5px 8px", fontFamily: "ui-monospace, monospace" }}>{l.hsn_sac || "—"}</td>
                )}
                <td style={{ padding: compact ? "3px 5px" : "5px 8px" }}>
                  {/* Feb 2026 R293zzc · Descriptions may now be rich
                      HTML (bullets, bold, line breaks). Render as HTML
                      when it looks like markup, otherwise render as
                      plain text to preserve newlines. */}
                  {/<[a-z][^>]*>/i.test(l.description || "")
                    ? <span dangerouslySetInnerHTML={{ __html: l.description }} />
                    : <span style={{ whiteSpace: "pre-wrap" }}>{l.description}</span>}
                </td>
                <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right" }}>{l.qty}</td>
                <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right" }}>{fmt(l.rate)}</td>
                {gstOn && (
                  <>
                    <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right" }}>{fmt(l.taxable)}</td>
                    <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right" }}>{l.gst_rate}%</td>
                    <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right" }}>{fmt(l.gst_amount)}</td>
                  </>
                )}
                <td style={{ padding: compact ? "3px 5px" : "5px 8px", textAlign: "right", fontWeight: 600 }}>{fmt(gstOn ? l.line_total : (l.taxable ?? (Number(l.qty || 0) * Number(l.rate || 0))))}</td>
              </tr>
            ))}
            {(!doc?.lines || doc.lines.length === 0) && (
              <tr><td colSpan={gstOn ? 9 : 5} style={{ padding: "8px", textAlign: "center", color: "#9ca3af" }}>No lines yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals block on the right */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: compact ? 8 : 12 }}>
        <table style={{ minWidth: compact ? "50%" : "45%", fontSize: compact ? "9px" : "11px", borderCollapse: "collapse" }}>
          <tbody>
            <PreviewRow label={gstOn ? "Taxable value" : "Subtotal"} value={t.total_taxable} compact={compact} format={fmt} />
            {showGstRows && (t.is_intra_state ? (
              <>
                <PreviewRow label="CGST" value={t.cgst_total} compact={compact} format={fmt} />
                <PreviewRow label={t.is_ut ? "UGST" : "SGST"} value={t.is_ut ? t.ugst_total : t.sgst_total} compact={compact} format={fmt} />
              </>
            ) : (
              <PreviewRow label="IGST" value={t.igst_total} compact={compact} format={fmt} />
            ))}
            {showGstRows && t.cess_total > 0 && <PreviewRow label="Cess" value={t.cess_total} compact={compact} format={fmt} />}
            {t.additional_charges > 0 && <PreviewRow label="Additional charges" value={t.additional_charges} compact={compact} format={fmt} />}
            {t.round_off !== 0 && t.round_off != null && <PreviewRow label="Round off" value={t.round_off} compact={compact} format={fmt} />}
            <PreviewRow label="Grand total" value={t.grand_total} bold bg="#f3f4f6" compact={compact} format={fmt} />
            {showGstRows && t.tds_amount > 0 && (
              <>
                <PreviewRow label={`Less: TDS (${doc?.tds_rate || 0}%)`} value={-t.tds_amount} compact={compact} format={fmt} />
                <PreviewRow label="Net receivable" value={t.net_receivable} bold bg="#f3f4f6" compact={compact} format={fmt} />
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Bank + terms + footer */}
      {/* R268w · FX reference block — only shown when doc currency
          differs from the workspace home currency. Helps the payer
          (and later, the reconciler) understand the equivalent home
          amount at the rate captured on the doc. */}
      {(() => {
        if (ratesHidden) return null;
        const home = profile?.home_currency || "INR";
        const docCurr = currency;
        const fxRate = doc?.fx_rate || doc?.totals?.fx_rate || 1;
        if (docCurr === home || fxRate === 1) return null;
        const homeTotal = doc?.totals?.grand_total_home
          ?? (Number(t.grand_total || 0) * fxRate);
        return (
          <div style={{ fontSize: compact ? "8.5px" : "10.5px", color: accent, marginBottom: compact ? 6 : 10 }}>
            Reference (workspace home): {fmtMoney(homeTotal, home)}
            {" · "}FX {fxRate.toFixed(4)} {docCurr}/{home}
          </div>
        );
      })()}
      {(() => {
        // Feb 2026 R293zza · Pick the bank account to show from the
        // profile's multi-bank list (currency match > default > first),
        // falling back to the legacy flat fields on the profile.
        //
        // Feb 2026 R293zze · If a specific account is picked (either by
        // explicit override or by currency match / default), use ONLY
        // that account's fields. Falling back to the legacy flat
        // `profile.bank_ifsc` etc. was leaking the primary INR account's
        // IFSC into foreign-currency invoices. The flat profile fallback
        // now applies only when the multi-bank list is empty.
        const list = Array.isArray(profile?.bank_accounts) ? profile.bank_accounts : [];
        let picked = null;
        if (list.length) {
          const dc = (doc?.currency || "").toUpperCase();
          const override = doc?.bank_account_id;
          picked = (override && list.find((a) => a.id === override))
                // Feb 2026 R293zze · Honour per-account default-for-currency
                // before loose currency match (one Mercury USD account can
                // be the deterministic USD + CAD default).
                || (dc && list.find((a) => (a.default_for_currency || [])
                    .map((c) => String(c).toUpperCase()).includes(dc)))
                || list.find((a) => (a.currency || "").toUpperCase() === dc)
                || list.find((a) => a.is_default)
                || list[0];
        }
        const bk = picked ? {
          bank_name: picked.bank_name,
          account_name: picked.account_name,
          account_number: picked.account_number,
          ifsc: picked.ifsc,
          branch: picked.branch,
          swift: picked.swift,
          iban: picked.iban,
          ach_routing: picked.ach_routing,
          fedwire_routing: picked.fedwire_routing,
          payment_method: picked.payment_method,
          beneficiary_address: picked.beneficiary_address,
          bank_address: picked.bank_address,
          upi_id: picked.upi_id,
          currency: picked.currency,
        } : {
          bank_name: profile?.bank_name,
          account_name: profile?.bank_account_name,
          account_number: profile?.bank_account,
          ifsc: profile?.bank_ifsc,
          branch: profile?.bank_branch,
          swift: undefined,
          iban: undefined,
          ach_routing: undefined,
          fedwire_routing: undefined,
          payment_method: undefined,
          beneficiary_address: undefined,
          bank_address: undefined,
          upi_id: profile?.upi_id,
          currency: undefined,
        };
        const show = bk.bank_name || bk.account_number || bk.upi_id || bk.swift;
        if (!show) return null;
        return (
          <div style={{ border: `1px dashed ${accent}`, padding: compact ? "5px 7px" : "8px 12px", borderRadius: 3, fontSize: compact ? "8.5px" : "10.5px", marginBottom: compact ? 6 : 10 }}>
            <div style={{ color: accent, fontSize: compact ? "8px" : "9.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Payment details{bk.currency && bk.currency !== "INR" ? ` · ${bk.currency}` : ""}
            </div>
            {bk.bank_name && (
              <div>
                <b>Bank:</b> {bk.bank_name}
                {bk.branch && <> · <b>Branch:</b> {bk.branch}</>}
              </div>
            )}
            {bk.payment_method && (
              <div><b>Payment method:</b> {bk.payment_method}</div>
            )}
            {bk.account_name && (
              <div><b>Account name:</b> {bk.account_name}</div>
            )}
            {bk.beneficiary_address && (
              <div><b>Beneficiary address:</b> {bk.beneficiary_address}</div>
            )}
            {bk.bank_address && bk.bank_address !== bk.beneficiary_address && (
              <div><b>Bank address:</b> {bk.bank_address}</div>
            )}
            {(bk.account_number || bk.ifsc) && (
              <div>
                {bk.account_number && <><b>A/c:</b> {bk.account_number}</>}
                {bk.account_number && bk.ifsc && " · "}
                {bk.ifsc && <><b>IFSC:</b> {bk.ifsc}</>}
              </div>
            )}
            {bk.ach_routing && <div><b>ACH routing:</b> {bk.ach_routing}</div>}
            {bk.fedwire_routing && <div><b>Fedwire routing:</b> {bk.fedwire_routing}</div>}
            {bk.swift && <div><b>SWIFT:</b> {bk.swift}{bk.iban && <> · <b>IBAN:</b> {bk.iban}</>}</div>}
            {!bk.swift && bk.iban && <div><b>IBAN:</b> {bk.iban}</div>}
            {bk.upi_id && <div><b>UPI:</b> {bk.upi_id}</div>}
          </div>
        );
      })()}
      {/* Feb 2026 R293zze · Per-doc Payment Instructions block —
          free-text notes (intermediary bank, reference numbers,
          installment plans) shown right under the bank block. */}
      {doc?.payment_instructions && (
        <div style={{ border: `1px dashed ${accent}`, padding: compact ? "5px 7px" : "8px 12px", borderRadius: 3, fontSize: compact ? "8.5px" : "10.5px", marginBottom: compact ? 6 : 10 }}>
          <div style={{ color: accent, fontSize: compact ? "8px" : "9.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Payment instructions
          </div>
          <div style={{ whiteSpace: "pre-line" }}>{doc.payment_instructions}</div>
        </div>
      )}
      {terms && (
        <div style={{ fontSize: compact ? "8.5px" : "10.5px", color: "#374151", marginBottom: compact ? 4 : 8, whiteSpace: "pre-line" }}>
          <b style={{ color: accent }}>Terms:</b> {terms}
        </div>
      )}
      {footer && (
        <div style={{ borderTop: `1px solid ${accent}`, paddingTop: compact ? 4 : 8, fontSize: compact ? "9px" : "11px", color: primary, textAlign: "center", fontStyle: "italic" }}>
          {footer}
        </div>
      )}
      {doc?.notes && !terms && (
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: compact ? 4 : 8, fontSize: compact ? "9px" : "11px", color: "#6b7280", whiteSpace: "pre-line" }}>
          {doc.notes}
        </div>
      )}
    </div>
  );
}

function PreviewRow({ label, value, bold, bg, compact, format }) {
  return (
    <tr style={{ background: bg || "transparent" }}>
      <td style={{ padding: compact ? "3px 6px" : "5px 8px", color: "#374151", fontWeight: bold ? 700 : 400 }}>{label}</td>
      <td style={{ padding: compact ? "3px 6px" : "5px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: bold ? 700 : 400 }}>
        {(format || inr)(value)}
      </td>
    </tr>
  );
}

/**
 * Small helper — sample data for a "canonical" invoice preview shown
 * on the Billing Setup screen so tweaks are visible immediately.
 */
export function samplePreviewDoc() {
  return {
    doc_type: "invoice",
    doc_no: "INV-2025-26-0001",
    doc_date: "2026-02-15",
    due_date: "2026-03-01",
    customer_name: "Acme Retainer Co.",
    customer_gstin: "27AAAAA0000A1Z5",
    customer_billing_address: "18 · Turbot Street\nMumbai 400001",
    place_of_supply_code: "27",
    reverse_charge: false,
    tds_rate: 10,
    lines: [
      { hsn_sac: "998314", description: "Retainer · Feb 2026", qty: 1,
        rate: 150000, taxable: 150000, gst_rate: 18, gst_amount: 27000,
        line_total: 177000 },
      { hsn_sac: "998363", description: "Meta / Google ads (media)", qty: 1,
        rate: 40000, taxable: 40000, gst_rate: 18, gst_amount: 7200,
        line_total: 47200 },
    ],
    totals: {
      total_taxable: 190000,
      is_intra_state: true,
      cgst_total: 17100, sgst_total: 17100,
      igst_total: 0, cess_total: 0,
      round_off: 0, additional_charges: 0,
      grand_total: 224200,
      tds_amount: 19000, net_receivable: 205200,
    },
  };
}
